import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HeartPulse, Users, ArrowRight, ShieldAlert } from "lucide-react";
import { GlobalButton, GlobalTextField } from "../../../../../global/forms/GlobalFormFields";
import { GlobalModal } from "../../../../../global/GlobalModal";
import { fetchSickLeaveSummary, updateAllSickLeaves } from "../../../api/attendanceApi";

export default function AttendanceSickLeavesSection({ onNotify }) {
  const [summary, setSummary] = useState({
    total_users: 0,
    min_sick_leaves: 0,
    max_sick_leaves: 0,
  });
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const rangeSpan = useMemo(() => {
    const min = summary.min_sick_leaves ?? 0;
    const max = summary.max_sick_leaves ?? 0;
    if (min === max) return `${min}`;
    return `${min}–${max}`;
  }, [summary.min_sick_leaves, summary.max_sick_leaves]);

  const rangePercent = useMemo(() => {
    const max = summary.max_sick_leaves ?? 0;
    return Math.min(100, Math.round((max / 30) * 100));
  }, [summary.max_sick_leaves]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSickLeaveSummary();
      setSummary(data);
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleOpenConfirm = (e) => {
    e?.preventDefault?.();
    setFormError("");
    const n = Number(count);
    if (!Number.isInteger(n) || n < 0 || n > 365) {
      setFormError("Enter a whole number between 0 and 365.");
      return;
    }
    setConfirmOpen(true);
  };

  const handleUpdateAll = async () => {
    const n = Number(count);
    setSaving(true);
    try {
      const result = await updateAllSickLeaves(n);
      onNotify?.(result.message || "Sick leaves updated for all users.", { variant: "success" });
      setConfirmOpen(false);
      setCount("");
      loadSummary();
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="att-sick-leaves" className="att-panel att-panel--sick">
      <div className="att-panel__head att-panel__head--compact">
        <div className="att-panel__icon" aria-hidden>
          <HeartPulse size={20} />
        </div>
        <div>
          <h3 className="att-panel__title">Sick leave sync</h3>
          <p className="att-panel__hint">
            Set the same available sick days for every active user in one go.
          </p>
        </div>
      </div>

      <div className="att-panel__body att-panel__body--sick">
        <div className="att-sick-stats">
          <div className="att-sick-stat">
            <span className="att-sick-stat__icon" aria-hidden>
              <Users size={16} />
            </span>
            <div>
              <strong>{loading ? "…" : summary.total_users}</strong>
              <span>Active users</span>
            </div>
          </div>

          <div className="att-sick-stat att-sick-stat--range">
            <div className="att-sick-stat__range-head">
              <strong>{loading ? "…" : rangeSpan}</strong>
              <span>Days currently (min–max)</span>
            </div>
            <div className="att-sick-stat__bar" aria-hidden>
              <span className="att-sick-stat__bar-fill" style={{ width: `${rangePercent}%` }} />
            </div>
          </div>
        </div>

        <form className="att-sick-composer" onSubmit={handleOpenConfirm}>
          <div className="att-sick-composer__label">
            <HeartPulse size={14} aria-hidden />
            Apply to everyone
          </div>

          <div className="att-sick-composer__field">
            <GlobalTextField
              label="New balance (days)"
              type="number"
              value={count}
              onChange={setCount}
              min={0}
              max={365}
              placeholder="e.g. 12"
              required
            />
          </div>

          <div className="att-sick-composer__actions">
            <GlobalButton
              type="submit"
              variant="primary"
              disabled={saving || loading}
            >
              {saving ? "Updating…" : "Update all"}
              {!saving ? <ArrowRight size={15} aria-hidden /> : null}
            </GlobalButton>
          </div>

          {formError ? (
            <p className="att-sick-composer__error" role="alert">
              {formError}
            </p>
          ) : null}
        </form>

        <p className="att-sick-note">
          <ShieldAlert size={14} aria-hidden />
          Overwrites each person&apos;s available sick leave — individual balances are not merged.
        </p>
      </div>

      <GlobalModal
        open={confirmOpen}
        title="Update all users?"
        onClose={() => !saving && setConfirmOpen(false)}
        footer={
          <>
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </GlobalButton>
            <GlobalButton type="button" variant="primary" onClick={handleUpdateAll} disabled={saving}>
              {saving ? "Updating…" : "Yes, update all"}
            </GlobalButton>
          </>
        }
      >
        <p>
          Set <strong>available sick leaves</strong> to <strong>{count}</strong> for all{" "}
          {summary.total_users} active user(s)?
        </p>
      </GlobalModal>
    </section>
  );
}
